import { useState, useEffect, useCallback, useMemo, memo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { DateInput } from "../components/ui/date-input";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import {
  Calendar, FileText, Shield, Home, Wrench, Receipt, AlertTriangle, Clock,
  Loader2, Search, RefreshCw, TrendingUp, TrendingDown, Download, CheckCircle2,
  Send, Plus, Trash2, Pencil, Euro, CreditCard, Car, Banknote, CalendarDays,
  ArrowUpRight, ArrowDownRight, Filter, Repeat, Timer, AlertCircle,
  Calculator, X, GripVertical, Landmark, Zap
} from "lucide-react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { useTranslation } from "../contexts/I18nContext";

const formatCurrency = (val) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(val || 0);

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    const parts = dateStr.split('-');
    if (parts[0].length === 4) {
      return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    } else {
      return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
    }
  } catch { return null; }
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    if (dateStr.includes('-')) {
      const parts = dateStr.split('-');
      if (parts[0].length === 4) return `${parts[2]}/${parts[1]}/${parts[0]}`;
      return `${parts[0]}/${parts[1]}/${parts[2]}`;
    }
    return new Date(dateStr).toLocaleDateString("it-IT");
  } catch { return dateStr; }
};

const getTipoConfig = (tipo) => {
  const configs = {
    fattura: { icon: FileText, color: "text-blue-600", bg: "bg-blue-50", label: "Fattura" },
    rata_fattura: { icon: CalendarDays, color: "text-purple-600", bg: "bg-purple-50", label: "Rata Fattura" },
    prestito: { icon: Banknote, color: "text-amber-600", bg: "bg-amber-50", label: "Prestito" },
    piano_ammortamento: { icon: Banknote, color: "text-amber-600", bg: "bg-amber-50", label: "Piano Ammort." },
    finanziamento: { icon: CreditCard, color: "text-indigo-600", bg: "bg-indigo-50", label: "Finanziamento" },
    leasing: { icon: Car, color: "text-cyan-600", bg: "bg-cyan-50", label: "Leasing" },
    assicurazione: { icon: Shield, color: "text-green-600", bg: "bg-green-50", label: "Assicurazione" },
    bollo: { icon: Receipt, color: "text-orange-600", bg: "bg-orange-50", label: "Bollo" },
    affitto: { icon: Home, color: "text-pink-600", bg: "bg-pink-50", label: "Affitto" },
    affitto_mensile: { icon: Home, color: "text-pink-600", bg: "bg-pink-50", label: "Affitti+Spese Cond." },
    spese_condominiali: { icon: Home, color: "text-cyan-600", bg: "bg-cyan-50", label: "Spese Cond." },
    bolletta_manuale: { icon: Zap, color: "text-yellow-700", bg: "bg-yellow-50", label: "Bolletta Man." },
    taratura: { icon: Wrench, color: "text-stone-600", bg: "bg-stone-50", label: "Taratura" },
    ricorrente: { icon: Repeat, color: "text-violet-600", bg: "bg-violet-50", label: "Ricorrente" },
    scadenza_programmata: { icon: Clock, color: "text-gray-600", bg: "bg-gray-50", label: "Programmata" },
    altro: { icon: Clock, color: "text-gray-600", bg: "bg-gray-50", label: "Altro" },
  };
  return configs[tipo] || configs.altro;
};

// Determines if an item is a credit (entrata) or debit (uscita)
const isCredito = (item) => {
  const tipoDoc = (item.dettaglio?.tipo_documento || '').toUpperCase();
  if (tipoDoc.includes('FV') || tipoDoc.includes('NCV') || tipoDoc.includes('NDV') || tipoDoc.includes('VENDITA')) return true;
  const flusso = item.dettaglio?.tipo_flusso || '';
  if (flusso === 'entrata') return true;
  if (item.importo < 0) return false; // negative = uscita from scheduled_payments
  return false;
};

// Identifies recurring items
const isRicorrente = (item) => {
  const tipo = item.tipo || '';
  if (['affitto', 'affitto_mensile', 'spese_condominiali', 'ricorrente'].includes(tipo)) return true;
  if (item.dettaglio?.frequenza) return true;
  if (item.dettaglio?.numero_rata && item.dettaglio?.totale_rate) return true;
  return false;
};

const ScadenzaTable = memo(function ScadenzaTable({ items, showCheckbox, selectedIds, onToggle, onToggleAll, onEdit, onDelete, onAddToPlanner, plannerItemIds, emptyMessage, showAll }) {
  const { t } = useTranslation();
  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const [visibleCount, setVisibleCount] = useState(80);

  // Reset visible count when items change
  useEffect(() => { setVisibleCount(80); }, [items]);

  if (items.length === 0) return (
    <div className="text-center py-8 text-muted-foreground">
      <Calendar className="h-10 w-10 mx-auto mb-2 opacity-40" />
      <p className="text-sm">{emptyMessage || "Nessuna scadenza"}</p>
    </div>
  );

  // Se showAll=true (ricerca attiva o export), mostra tutti gli elementi senza paginazione
  const effectiveVisible = showAll ? items.length : visibleCount;
  const visible = items.slice(0, effectiveVisible);
  const hasMore = !showAll && visibleCount < items.length;

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            {showCheckbox && (
              <TableHead className="w-10">
                <Checkbox
                  checked={selectedIds?.size === items.length && items.length > 0}
                  onCheckedChange={onToggleAll}
                />
              </TableHead>
            )}
            <TableHead className="w-10">{t('common.flow')}</TableHead>
            <TableHead>{t("common.type")}</TableHead>
            <TableHead>{t("common.description")}</TableHead>
            <TableHead>{t('common.beneficiary')}</TableHead>
            <TableHead className="text-right">{t("common.amount")}</TableHead>
            <TableHead>{t('invoices.dueDate')}</TableHead>
            <TableHead>{t("common.status")}</TableHead>
            <TableHead className="w-24"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {visible.map((item) => {
            const config = getTipoConfig(item.tipo);
            const scadDate = parseDate(item.data_scadenza);
            const days = scadDate ? Math.ceil((scadDate - today) / (1000 * 60 * 60 * 24)) : null;
            const overdue = item.stato !== 'pagato' && item.stato !== 'pagata' && scadDate && scadDate < today;
            const credito = isCredito(item);
            const absImporto = Math.abs(item.importo || 0);
            const inPlanner = plannerItemIds?.has(item._uid);

            return (
              <TableRow key={item._uid} className={overdue ? 'bg-red-50/60' : ''} data-testid={`scadenza-row-${item._uid}`}>
                {showCheckbox && (
                  <TableCell>
                    <Checkbox
                      checked={selectedIds?.has(item._uid)}
                      onCheckedChange={() => onToggle?.(item._uid)}
                      disabled={item.stato === 'pagato' || item.stato === 'pagata'}
                    />
                  </TableCell>
                )}
                <TableCell>
                  {credito ? (
                    <ArrowUpRight className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <ArrowDownRight className="h-4 w-4 text-red-600" />
                  )}
                </TableCell>
                <TableCell>
                  <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full ${config.bg}`}>
                    <config.icon className={`h-3 w-3 ${config.color}`} />
                    <span className={`text-xs font-medium ${config.color}`}>{config.label}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="font-medium text-sm">{item.descrizione}</div>
                  {item.dettaglio?.numero_rata && item.dettaglio?.totale_rate && (
                    <span className="text-xs text-muted-foreground">Rata {item.dettaglio.numero_rata}/{item.dettaglio.totale_rate}</span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{item.beneficiario || '-'}</TableCell>
                <TableCell className={`text-right font-semibold tabular-nums ${credito ? 'text-emerald-600' : 'text-red-600'}`}>
                  {credito ? '+' : '-'}{formatCurrency(absImporto)}
                </TableCell>
                <TableCell>
                  <div className="text-sm">{formatDate(item.data_scadenza)}</div>
                  {item.stato !== 'pagato' && item.stato !== 'pagata' && days !== null && (
                    <span className={`text-xs ${overdue ? 'text-red-600 font-medium' : days <= 7 ? 'text-orange-600' : 'text-muted-foreground'}`}>
                      {overdue ? `${Math.abs(days)} gg fa` : days === 0 ? 'Oggi' : `tra ${days} gg`}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {item.stato === 'pagato' || item.stato === 'pagata' ? (
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs">{t('invoices.paid')}</Badge>
                  ) : overdue ? (
                    <Badge className="bg-red-100 text-red-700 text-xs">{t('common.expired')}</Badge>
                  ) : (
                    <Badge className="bg-blue-100 text-blue-700 text-xs">{t('common.toPay')}</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex gap-0.5 items-center">
                    {onAddToPlanner && !inPlanner && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-indigo-600 hover:bg-indigo-50" onClick={() => onAddToPlanner(item)} title={t("scheduler.addToPlanner")} data-testid={`add-planner-row-${item._uid}`}>
                        <Calculator className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {inPlanner && (
                      <Badge className="bg-indigo-100 text-indigo-700 text-[10px] px-1.5">P</Badge>
                    )}
                    {item._source === 'scheduled' && item.stato !== 'pagato' && item.stato !== 'pagata' && (
                      <>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit?.(item)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => onDelete?.(item)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {hasMore && (
        <div className="text-center py-3">
          <Button variant="ghost" size="sm" onClick={() => setVisibleCount(v => v + 100)} data-testid="load-more-btn">
            Mostra altri ({items.length - visibleCount} rimanenti)
          </Button>
        </div>
      )}
    </>
  );
});

function SummaryCard({ title, icon: Icon, iconBg, iconColor, crediti, debiti, count }) {
  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`summary-${title.toLowerCase().replace(/\s+/g,'-')}`}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between mb-3">
          <div className={`h-10 w-10 rounded-xl ${iconBg} flex items-center justify-center`}>
            <Icon className={`h-5 w-5 ${iconColor}`} />
          </div>
          <span className="text-xs text-muted-foreground">{count} scadenze</span>
        </div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{title}</p>
        <div className="flex items-baseline gap-3">
          <div>
            <span className="text-xs text-red-500">Debiti </span>
            <span className="text-base font-bold tabular-nums text-red-600">{formatCurrency(debiti)}</span>
          </div>
          {crediti > 0 && (
            <div>
              <span className="text-xs text-emerald-500">Crediti </span>
              <span className="text-base font-bold tabular-nums text-emerald-600">{formatCurrency(crediti)}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function ScadenziarioUnificatoPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [scadenzeRaw, setScadenzeRaw] = useState([]);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [dataInizio, setDataInizio] = useState(`01-01-${currentYear}`);
  const [dataFine, setDataFine] = useState(`31-12-${currentYear}`);
  const [activeView, setActiveView] = useState("panoramica");
  const [searchText, setSearchText] = useState("");

  // Liquidazione IVA
  const [ivaLoading, setIvaLoading] = useState(false);
  const [ivaAnno, setIvaAnno] = useState(currentYear);
  const [ivaMese, setIvaMese] = useState(currentMonth);
  const [liquidazioneIva, setLiquidazioneIva] = useState(null);

  // Selection & dialogs
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [approving, setApproving] = useState(false);
  const [addDialog, setAddDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    descrizione: "", importo: "", data_scadenza: "", tipo: "altro",
    beneficiario: "", tipo_flusso: "uscita", numero_rata: "", totale_rate: "", note: ""
  });

  // Planner Liquidità
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [plannerItems, setPlannerItems] = useState([]);
  const [bancheData, setBancheData] = useState([]);
  const [saldoManuale, setSaldoManuale] = useState(null); // null = usa saldo da API
  const [plannerLoading, setPlannerLoading] = useState(false);

  const fetchBanche = useCallback(async () => {
    setPlannerLoading(true);
    try {
      const prefix = getApiPrefix();
      const res = await api.get(`${prefix}/tesoreria/riepilogo-saldi`);
      setBancheData(res.data?.banche || []);
      setSaldoManuale(null);
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error('Fetch banche failed:', err); }
    finally { setPlannerLoading(false); }
  }, []);

  useEffect(() => {
    if (plannerOpen) fetchBanche();
  }, [plannerOpen, fetchBanche]);

  const saldoIniziale = useMemo(() => {
    if (saldoManuale !== null) return saldoManuale;
    return bancheData.reduce((sum, b) => sum + (b.saldo || 0), 0);
  }, [saldoManuale, bancheData]);

  const plannerRunning = useMemo(() => {
    let balance = saldoIniziale;
    return plannerItems.map(item => {
      const credito = isCredito(item);
      const abs = Math.abs(item.importo || 0);
      balance = credito ? balance + abs : balance - abs;
      return { ...item, _runningBalance: balance };
    });
  }, [plannerItems, saldoIniziale]);

  const saldoFinale = plannerRunning.length > 0
    ? plannerRunning[plannerRunning.length - 1]._runningBalance
    : saldoIniziale;

  const addToPlannerFromSelection = () => {
    const itemsToAdd = currentViewItems
      .filter(s => selectedIds.has(s._uid))
      .filter(s => !plannerItems.some(p => p._uid === s._uid));
    if (itemsToAdd.length === 0) return;
    const merged = [...plannerItems, ...itemsToAdd].sort((a, b) => {
      const da = parseDate(a.data_scadenza);
      const db2 = parseDate(b.data_scadenza);
      return (da || new Date(0)) - (db2 || new Date(0));
    });
    setPlannerItems(merged);
    if (!plannerOpen) setPlannerOpen(true);
    showSuccess(`${itemsToAdd.length} scadenze aggiunte al planner`);
  };

  const addSingleToPlanner = useCallback((item) => {
    if (plannerItems.some(p => p._uid === item._uid)) return;
    const merged = [...plannerItems, item].sort((a, b) => {
      const da = parseDate(a.data_scadenza);
      const db2 = parseDate(b.data_scadenza);
      return (da || new Date(0)) - (db2 || new Date(0));
    });
    setPlannerItems(merged);
    if (!plannerOpen) setPlannerOpen(true);
  }, [plannerItems, plannerOpen]);

  const plannerItemIds = useMemo(() => new Set(plannerItems.map(p => p._uid)), [plannerItems]);

  const removePlannerItem = (uid) => {
    setPlannerItems(prev => prev.filter(p => p._uid !== uid));
  };

  const convertDate = (d) => {
    if (!d) return '';
    const parts = d.split('-');
    if (parts[0].length === 2) return `${parts[2]}-${parts[1]}-${parts[0]}`;
    return d;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const prefix = getApiPrefix();
      const params = new URLSearchParams();
      if (dataInizio) params.append("data_inizio", convertDate(dataInizio));
      if (dataFine) params.append("data_fine", convertDate(dataFine));

      // SINGLE source of truth: the scadenziario report already includes scheduled_payments
      const res = await api.get(`${prefix}/reports/scadenziario?${params.toString()}`);
      const items = (res.data?.scadenze || []).map((s, idx) => ({
        ...s,
        _uid: `${s.tipo}-${s.id}-${idx}`,
        _source: s.dettaglio?.numero_rata !== undefined && s.dettaglio?.totale_rate !== undefined
          ? 'scheduled' : (s.tipo === 'fattura' || s.tipo === 'rata_fattura' ? 'fattura' : 'generated')
      }));
      setScadenzeRaw(items);
    } catch (err) {
      handleApiError(err, t("errors.loadScheduler"));
    } finally {
      setLoading(false);
    }
  }, [dataInizio, dataFine, t]);

  const fetchLiquidazioneIva = useCallback(async () => {
    setIvaLoading(true);
    try {
      const prefix = getApiPrefix();
      const res = await api.get(`${prefix}/reports/liquidazione-iva?anno=${ivaAnno}&mese=${ivaMese}`);
      setLiquidazioneIva(res.data);
    } catch (err) {
      handleApiError(err, t("errors.loadVatSettlement"));
    } finally {
      setIvaLoading(false);
    }
  }, [ivaAnno, ivaMese, t]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (activeView === "liquidazione-iva") fetchLiquidazioneIva();
  }, [activeView, fetchLiquidazioneIva]);

  // Filter by search
  const filtered = useMemo(() => {
    if (!searchText) return scadenzeRaw;
    const s = searchText.toLowerCase();
    return scadenzeRaw.filter(item => {
      if (item.descrizione?.toLowerCase().includes(s)) return true;
      if (item.beneficiario?.toLowerCase().includes(s)) return true;
      // Cerca anche nei dettagli (proprietari appartamenti, note, veicoli, ecc.)
      const d = item.dettaglio;
      if (d) {
        if (typeof d.note === 'string' && d.note.toLowerCase().includes(s)) return true;
        if (typeof d.veicolo === 'string' && d.veicolo.toLowerCase().includes(s)) return true;
        if (typeof d.appartamento === 'string' && d.appartamento.toLowerCase().includes(s)) return true;
        if (typeof d.indirizzo === 'string' && d.indirizzo.toLowerCase().includes(s)) return true;
        if (Array.isArray(d.appartamenti)) {
          for (const a of d.appartamenti) {
            if ((a.appartamento || '').toLowerCase().includes(s)) return true;
            if ((a.indirizzo || '').toLowerCase().includes(s)) return true;
            if ((a.proprietario || '').toLowerCase().includes(s)) return true;
          }
        }
      }
      return false;
    });
  }, [scadenzeRaw, searchText]);

  // Categorize
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const categorized = useMemo(() => {
    const scaduti = [];
    const entro30 = [];
    const entro60 = [];
    const entro90 = [];
    const oltre90 = [];
    const ricorrenti = [];

    const d30 = new Date(today); d30.setDate(d30.getDate() + 30);
    const d60 = new Date(today); d60.setDate(d60.getDate() + 60);
    const d90 = new Date(today); d90.setDate(d90.getDate() + 90);

    filtered.forEach(item => {
      const isPaid = item.stato === 'pagato' || item.stato === 'pagata';
      const dt = parseDate(item.data_scadenza);

      if (isRicorrente(item)) ricorrenti.push(item);

      if (isPaid) return; // don't categorize paid items in time buckets

      if (dt && dt < today) {
        scaduti.push(item);
      } else if (dt && dt <= d30) {
        entro30.push(item);
      } else if (dt && dt <= d60) {
        entro60.push(item);
      } else if (dt && dt <= d90) {
        entro90.push(item);
      } else {
        oltre90.push(item);
      }
    });

    const calcTotals = (arr) => {
      let crediti = 0, debiti = 0;
      arr.forEach(i => {
        const abs = Math.abs(i.importo || 0);
        if (isCredito(i)) crediti += abs;
        else debiti += abs;
      });
      return { crediti, debiti, count: arr.length };
    };

    return {
      scaduti: { items: scaduti, ...calcTotals(scaduti) },
      entro30: { items: entro30, ...calcTotals(entro30) },
      entro60: { items: entro60, ...calcTotals(entro60) },
      entro90: { items: entro90, ...calcTotals(entro90) },
      oltre90: { items: oltre90, ...calcTotals(oltre90) },
      ricorrenti: { items: ricorrenti, ...calcTotals(ricorrenti) },
    };
  }, [filtered, today]);

  // Selection
  const currentViewItems = useMemo(() => {
    switch (activeView) {
      case 'scaduti': return categorized.scaduti.items;
      case 'entro30': return categorized.entro30.items;
      case 'entro60': return categorized.entro60.items;
      case 'entro90': return categorized.entro90.items;
      case 'ricorrenti': return categorized.ricorrenti.items;
      default: return filtered;
    }
  }, [activeView, categorized, filtered]);

  const toggleSelection = (uid) => {
    const n = new Set(selectedIds);
    n.has(uid) ? n.delete(uid) : n.add(uid);
    setSelectedIds(n);
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === currentViewItems.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(currentViewItems.map(s => s._uid)));
  };

  // Approve payment
  const handleApprovePayment = async () => {
    if (selectedIds.size === 0) return;
    setApproving(true);
    try {
      const prefix = getApiPrefix();
      const selected = currentViewItems.filter(s => selectedIds.has(s._uid));

      const fatture = selected.filter(s => s._source === 'fattura');
      const programmate = selected.filter(s => s._source === 'scheduled');
      const others = selected.filter(s => s._source === 'generated');

      if (fatture.length > 0) {
        await api.post(`${prefix}/reports/scadenziario/approva-pagamento`, { scadenze: fatture });
      }

      for (const s of programmate) {
        try {
          await api.patch(`${prefix}/scheduled-payments/${s.id}/pay`);
          await api.post(`${prefix}/tesoreria/aggiungi-programmata`, {
            scheduled_payment_id: s.id,
            descrizione: s.descrizione,
            importo: Math.abs(s.importo),
            data_scadenza: s.data_scadenza,
            beneficiario: s.beneficiario,
            tipo: s.tipo
          });
        } catch (e) { if (process.env.NODE_ENV !== 'production') console.error('Approval failed for item:', e); }
      }

      showSuccess(`${selected.length} scadenze approvate per pagamento`);
      setSelectedIds(new Set());
      setApprovalDialog(false);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorApprovePayment"));
    } finally {
      setApproving(false);
    }
  };

  // Save scheduled payment
  const handleSaveScheduled = async () => {
    if (!form.descrizione || !form.importo || !form.data_scadenza) {
      handleApiError({ message: t("errors.requiredFields") });
      return;
    }
    try {
      const prefix = getApiPrefix();
      const payload = {
        descrizione: form.descrizione,
        importo: parseFloat(form.importo),
        data_scadenza: form.data_scadenza,
        tipo: form.tipo,
        beneficiario: form.beneficiario || null,
        tipo_flusso: form.tipo_flusso,
        numero_rata: form.numero_rata ? parseInt(form.numero_rata) : null,
        totale_rate: form.totale_rate ? parseInt(form.totale_rate) : null,
        note: form.note || null
      };
      if (editingItem) {
        await api.put(`${prefix}/scheduled-payments/${editingItem.id}`, payload);
        showSuccess(t("success.deadlineUpdated"));
      } else {
        await api.post(`${prefix}/scheduled-payments`, payload);
        showSuccess(t("success.deadlineCreated"));
      }
      setAddDialog(false);
      setEditingItem(null);
      resetForm();
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    }
  };

  const handleDelete = async (item) => {
    if (item._source !== 'scheduled') return;
    if (!window.confirm(t("confirm.deleteDeadline"))) return;
    try {
      const prefix = getApiPrefix();
      await api.delete(`${prefix}/scheduled-payments/${item.id}`);
      showSuccess(t("success.deadlineDeleted"));
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    }
  };

  const resetForm = () => {
    setForm({ descrizione: "", importo: "", data_scadenza: "", tipo: "altro",
      beneficiario: "", tipo_flusso: "uscita", numero_rata: "", totale_rate: "", note: "" });
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setForm({
      descrizione: item.descrizione || "",
      importo: Math.abs(item.importo)?.toString() || "",
      data_scadenza: item.data_scadenza || "",
      tipo: item.tipo || "altro",
      beneficiario: item.beneficiario || "",
      tipo_flusso: item.dettaglio?.tipo_flusso || "uscita",
      numero_rata: item.dettaglio?.numero_rata?.toString() || "",
      totale_rate: item.dettaglio?.totale_rate?.toString() || "",
      note: item.dettaglio?.note || ""
    });
    setAddDialog(true);
  };

  const renderViewContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (activeView === "panoramica") {
      return (
        <div className="space-y-6">
          {/* KPI cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <SummaryCard
              title={t("scheduler.overdue")}
              icon={AlertCircle}
              iconBg="bg-red-100"
              iconColor="text-red-600"
              crediti={categorized.scaduti.crediti}
              debiti={categorized.scaduti.debiti}
              count={categorized.scaduti.count}
            />
            <SummaryCard
              title={t("scheduler.within30")}
              icon={Timer}
              iconBg="bg-amber-100"
              iconColor="text-amber-600"
              crediti={categorized.entro30.crediti}
              debiti={categorized.entro30.debiti}
              count={categorized.entro30.count}
            />
            <SummaryCard
              title={t("scheduler.within60")}
              icon={Clock}
              iconBg="bg-blue-100"
              iconColor="text-blue-600"
              crediti={categorized.entro60.crediti}
              debiti={categorized.entro60.debiti}
              count={categorized.entro60.count}
            />
            <SummaryCard
              title={t("scheduler.within90")}
              icon={CalendarDays}
              iconBg="bg-indigo-100"
              iconColor="text-indigo-600"
              crediti={categorized.entro90.crediti}
              debiti={categorized.entro90.debiti}
              count={categorized.entro90.count}
            />
          </div>

          {/* Scaduti section */}
          {categorized.scaduti.count > 0 && (
            <Card className="border-red-200" data-testid="section-scaduti">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-red-700">
                  <AlertCircle className="h-4 w-4" />
                  Crediti e Debiti Scaduti ({categorized.scaduti.count})
                </CardTitle>
                <CardDescription>
                  Debiti: {formatCurrency(categorized.scaduti.debiti)}
                  {categorized.scaduti.crediti > 0 && <> | Crediti: {formatCurrency(categorized.scaduti.crediti)}</>}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <ScadenzaTable
                  items={categorized.scaduti.items}
                  showCheckbox
                  selectedIds={selectedIds}
                  onToggle={toggleSelection}
                  onToggleAll={toggleSelectAll}
                  onEdit={openEditDialog}
                  onDelete={handleDelete}
                  onAddToPlanner={addSingleToPlanner}
                  plannerItemIds={plannerItemIds}
                  emptyMessage="Nessuna scadenza scaduta"
                  showAll={!!searchText}
                />
              </CardContent>
            </Card>
          )}

          {/* In scadenza 30gg */}
          {categorized.entro30.count > 0 && (
            <Card className="border-amber-200" data-testid="section-entro30">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-amber-700">
                  <Timer className="h-4 w-4" />
                  In Scadenza entro 30 giorni ({categorized.entro30.count})
                </CardTitle>
                <CardDescription>
                  Debiti: {formatCurrency(categorized.entro30.debiti)}
                  {categorized.entro30.crediti > 0 && <> | Crediti: {formatCurrency(categorized.entro30.crediti)}</>}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <ScadenzaTable
                  items={categorized.entro30.items}
                  showCheckbox
                  selectedIds={selectedIds}
                  onToggle={toggleSelection}
                  onToggleAll={toggleSelectAll}
                  onEdit={openEditDialog}
                  onDelete={handleDelete}
                  onAddToPlanner={addSingleToPlanner}
                  plannerItemIds={plannerItemIds}
                  showAll={!!searchText}
                />
              </CardContent>
            </Card>
          )}

          {/* Ricorrenti */}
          {categorized.ricorrenti.count > 0 && (
            <Card className="border-violet-200" data-testid="section-ricorrenti">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-violet-700">
                  <Repeat className="h-4 w-4" />
                  Scadenze Ricorrenti ({categorized.ricorrenti.count})
                </CardTitle>
                <CardDescription>
                  Debiti: {formatCurrency(categorized.ricorrenti.debiti)}
                  {categorized.ricorrenti.crediti > 0 && <> | Crediti: {formatCurrency(categorized.ricorrenti.crediti)}</>}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <ScadenzaTable
                  items={categorized.ricorrenti.items}
                  showCheckbox={false}
                  onEdit={openEditDialog}
                  onDelete={handleDelete}
                  onAddToPlanner={addSingleToPlanner}
                  plannerItemIds={plannerItemIds}
                  showAll={!!searchText}
                />
              </CardContent>
            </Card>
          )}

          {categorized.scaduti.count === 0 && categorized.entro30.count === 0 && categorized.ricorrenti.count === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-emerald-400" />
                <p>{t("scheduler.noUrgentDeadlines")}</p>
              </CardContent>
            </Card>
          )}
        </div>
      );
    }

    // Dedicated views
    const viewData = {
      scaduti: { items: categorized.scaduti.items, title: "Crediti e Debiti Scaduti", icon: AlertCircle, desc: categorized.scaduti },
      entro30: { items: categorized.entro30.items, title: "In Scadenza entro 30 giorni", icon: Timer, desc: categorized.entro30 },
      entro60: { items: categorized.entro60.items, title: "In Scadenza entro 60 giorni", icon: Clock, desc: categorized.entro60 },
      entro90: { items: categorized.entro90.items, title: "In Scadenza entro 90 giorni", icon: CalendarDays, desc: categorized.entro90 },
      ricorrenti: { items: categorized.ricorrenti.items, title: "Scadenze Ricorrenti", icon: Repeat, desc: categorized.ricorrenti },
    }[activeView];

    if (!viewData) return null;

    const crediti = viewData.items.filter(i => isCredito(i));
    const debiti = viewData.items.filter(i => !isCredito(i));

    return (
      <div className="space-y-6">
        {/* KPI */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="pt-5 pb-4">
              <p className="text-xs font-semibold text-red-600 uppercase tracking-wider">Debiti</p>
              <p className="text-2xl font-bold tabular-nums text-red-600 mt-1">{formatCurrency(viewData.desc.debiti)}</p>
              <p className="text-xs text-muted-foreground">{debiti.length} scadenze</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="pt-5 pb-4">
              <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Crediti</p>
              <p className="text-2xl font-bold tabular-nums text-emerald-600 mt-1">{formatCurrency(viewData.desc.crediti)}</p>
              <p className="text-xs text-muted-foreground">{crediti.length} scadenze</p>
            </CardContent>
          </Card>
          <Card className={`border-l-4 ${viewData.desc.crediti - viewData.desc.debiti >= 0 ? 'border-l-emerald-500' : 'border-l-red-500'}`}>
            <CardContent className="pt-5 pb-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('common.netBalance')}</p>
              <p className={`text-2xl font-bold tabular-nums mt-1 ${viewData.desc.crediti - viewData.desc.debiti >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {formatCurrency(viewData.desc.crediti - viewData.desc.debiti)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Debiti */}
        <Card data-testid={`view-${activeView}-debiti`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-red-700">
              <ArrowDownRight className="h-4 w-4" />
              Debiti ({debiti.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScadenzaTable
              items={debiti}
              showCheckbox={activeView !== 'ricorrenti'}
              selectedIds={selectedIds}
              onToggle={toggleSelection}
              onToggleAll={() => {
                const debitIds = debiti.map(d => d._uid);
                if (debitIds.every(id => selectedIds.has(id))) {
                  const n = new Set(selectedIds);
                  debitIds.forEach(id => n.delete(id));
                  setSelectedIds(n);
                } else {
                  setSelectedIds(new Set([...selectedIds, ...debitIds]));
                }
              }}
              onEdit={openEditDialog}
              onDelete={handleDelete}
              onAddToPlanner={addSingleToPlanner}
              plannerItemIds={plannerItemIds}
              emptyMessage="Nessun debito"
              showAll={!!searchText}
            />
          </CardContent>
        </Card>

        {/* Crediti */}
        <Card data-testid={`view-${activeView}-crediti`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-emerald-700">
              <ArrowUpRight className="h-4 w-4" />
              Crediti ({crediti.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScadenzaTable
              items={crediti}
              showCheckbox={activeView !== 'ricorrenti'}
              selectedIds={selectedIds}
              onToggle={toggleSelection}
              onToggleAll={() => {
                const creditIds = crediti.map(c => c._uid);
                if (creditIds.every(id => selectedIds.has(id))) {
                  const n = new Set(selectedIds);
                  creditIds.forEach(id => n.delete(id));
                  setSelectedIds(n);
                } else {
                  setSelectedIds(new Set([...selectedIds, ...creditIds]));
                }
              }}
              onEdit={openEditDialog}
              onDelete={handleDelete}
              onAddToPlanner={addSingleToPlanner}
              plannerItemIds={plannerItemIds}
              emptyMessage="Nessun credito"
              showAll={!!searchText}
            />
          </CardContent>
        </Card>
      </div>
    );
  };

  const showActionBar = activeView !== 'liquidazione-iva';

  return (
    <div className="space-y-4" data-testid="scadenziario-unificato">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-white" />
            </div>
            Scadenziario
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Vista riepilogativa crediti, debiti e scadenze ricorrenti</p>
        </div>
        <div className="flex gap-2">
          {showActionBar && (
            <>
              <Button
                variant={plannerOpen ? "default" : "outline"}
                onClick={() => setPlannerOpen(o => !o)}
                className={plannerOpen ? "gap-2 bg-indigo-600 hover:bg-indigo-700" : "gap-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50"}
                data-testid="planner-btn"
              >
                <Calculator className="h-4 w-4" />
                Planner
                {plannerItems.length > 0 && (
                  <Badge className={`ml-1 h-5 text-[10px] px-1.5 ${plannerOpen ? 'bg-white/20 text-white' : 'bg-indigo-600 text-white'}`}>{plannerItems.length}</Badge>
                )}
              </Button>
              <Button variant="outline" onClick={fetchData} disabled={loading} className="gap-2" data-testid="refresh-btn">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />{t('common.refresh')}</Button>
              <Button onClick={() => { resetForm(); setEditingItem(null); setAddDialog(true); }} className="gap-2" data-testid="add-scadenza-btn">
                <Plus className="h-4 w-4" />{t('invoices.newDueDate')}</Button>
            </>
          )}
        </div>
      </div>

      {/* Main layout: content + inline planner panel */}
      <div className="flex gap-4 items-start">
        {/* Main content area */}
        <div className={`min-w-0 space-y-4 transition-all duration-200 ${plannerOpen ? 'flex-1' : 'w-full'}`}>
          <Tabs value={activeView} onValueChange={(v) => { setActiveView(v); setSelectedIds(new Set()); }} className="space-y-4">
            <TabsList className="flex flex-wrap h-auto gap-1 bg-muted/50 p-1">
              <TabsTrigger value="panoramica" className="gap-1.5 text-xs" data-testid="tab-panoramica">
                <Calendar className="h-3.5 w-3.5" />{t('common.overview')}</TabsTrigger>
              <TabsTrigger value="scaduti" className="gap-1.5 text-xs" data-testid="tab-scaduti">
                <AlertCircle className="h-3.5 w-3.5" /> Scaduti
                {categorized.scaduti.count > 0 && (
                  <Badge variant="destructive" className="ml-1 h-5 text-[10px] px-1.5">{categorized.scaduti.count}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="entro30" className="gap-1.5 text-xs" data-testid="tab-entro30">
                <Timer className="h-3.5 w-3.5" /> 30 gg
                {categorized.entro30.count > 0 && (
                  <Badge className="ml-1 h-5 text-[10px] px-1.5 bg-amber-100 text-amber-700">{categorized.entro30.count}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="entro60" className="gap-1.5 text-xs" data-testid="tab-entro60">
                <Clock className="h-3.5 w-3.5" /> 60 gg
              </TabsTrigger>
              <TabsTrigger value="entro90" className="gap-1.5 text-xs" data-testid="tab-entro90">
                <CalendarDays className="h-3.5 w-3.5" /> 90 gg
              </TabsTrigger>
              <TabsTrigger value="ricorrenti" className="gap-1.5 text-xs" data-testid="tab-ricorrenti">
                <Repeat className="h-3.5 w-3.5" /> Ricorrenti
              </TabsTrigger>
              <TabsTrigger value="liquidazione-iva" className="gap-1.5 text-xs" data-testid="tab-iva">
                <Receipt className="h-3.5 w-3.5" /> Liquidazione IVA
              </TabsTrigger>
            </TabsList>

            {/* Filters (not for IVA) */}
            {showActionBar && (
              <Card className="shadow-sm">
                <CardContent className="pt-4 pb-3">
                  <div className="flex flex-wrap gap-4 items-end">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Da</Label>
                      <DateInput value={dataInizio} onChange={(e) => setDataInizio(e.target.value)} className="w-36" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">A</Label>
                      <DateInput value={dataFine} onChange={(e) => setDataFine(e.target.value)} className="w-36" />
                    </div>
                    <div className="space-y-1 flex-1 min-w-[200px]">
                      <Label className="text-xs text-muted-foreground">{t("common.search")}</Label>
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          placeholder="Descrizione, beneficiario..."
                          className="pl-8"
                          data-testid="search-input"
                        />
                      </div>
                    </div>
                    <Button variant="outline" onClick={fetchData} disabled={loading} size="sm">
                      <Filter className="h-4 w-4 mr-1.5" />{t('common.filter')}</Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Selection bar */}
            {selectedIds.size > 0 && showActionBar && (
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="py-3 flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-700">
                    {selectedIds.size} scadenze selezionate
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())}>{t('common.deselect')}</Button>
                    <Button size="sm" variant="outline" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50" onClick={addToPlannerFromSelection} data-testid="add-to-planner-btn">
                      <Calculator className="h-4 w-4 mr-2" /> Aggiungi al Planner
                    </Button>
                    <Button size="sm" onClick={() => setApprovalDialog(true)}>
                      <Send className="h-4 w-4 mr-2" />{t('common.approvePayment')}</Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Views */}
            <TabsContent value="panoramica">{renderViewContent()}</TabsContent>
            <TabsContent value="scaduti">{renderViewContent()}</TabsContent>
            <TabsContent value="entro30">{renderViewContent()}</TabsContent>
            <TabsContent value="entro60">{renderViewContent()}</TabsContent>
            <TabsContent value="entro90">{renderViewContent()}</TabsContent>
            <TabsContent value="ricorrenti">{renderViewContent()}</TabsContent>

        {/* IVA Tab */}
        <TabsContent value="liquidazione-iva" className="space-y-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t("common.year")}</Label>
                  <Select value={String(ivaAnno)} onValueChange={(v) => setIvaAnno(parseInt(v))}>
                    <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('common.month')}</Label>
                  <Select value={String(ivaMese)} onValueChange={(v) => setIvaMese(parseInt(v))}>
                    <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[
                        { v: 1, l: "Gennaio" }, { v: 2, l: "Febbraio" }, { v: 3, l: "Marzo" },
                        { v: 4, l: "Aprile" }, { v: 5, l: "Maggio" }, { v: 6, l: "Giugno" },
                        { v: 7, l: "Luglio" }, { v: 8, l: "Agosto" }, { v: 9, l: "Settembre" },
                        { v: 10, l: "Ottobre" }, { v: 11, l: "Novembre" }, { v: 12, l: "Dicembre" }
                      ].map(m => (
                        <SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={fetchLiquidazioneIva} disabled={ivaLoading}>
                  <Filter className="h-4 w-4 mr-2" />{t('common.calculate')}</Button>
              </div>
            </CardContent>
          </Card>

          {ivaLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : liquidazioneIva ? (
            <>
              <div className="grid gap-4 md:grid-cols-3">
                <Card className="border-l-4 border-l-red-500">
                  <CardContent className="pt-5 pb-4">
                    <p className="text-xs font-semibold text-red-600 uppercase tracking-wider">IVA a Debito</p>
                    <p className="text-2xl font-bold tabular-nums text-red-600 mt-1">{formatCurrency(liquidazioneIva.iva_debito?.totale || 0)}</p>
                    <p className="text-xs text-muted-foreground">{liquidazioneIva.iva_debito?.count || 0} fatture vendita</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-emerald-500">
                  <CardContent className="pt-5 pb-4">
                    <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">IVA a Credito</p>
                    <p className="text-2xl font-bold tabular-nums text-emerald-600 mt-1">{formatCurrency(liquidazioneIva.iva_credito?.totale || 0)}</p>
                    <p className="text-xs text-muted-foreground">{liquidazioneIva.iva_credito?.count || 0} fatture acquisto</p>
                  </CardContent>
                </Card>
                <Card className={`border-l-4 ${liquidazioneIva.saldo_iva > 0 ? 'border-l-red-500 bg-red-50/50' : liquidazioneIva.saldo_iva < 0 ? 'border-l-emerald-500 bg-emerald-50/50' : 'border-l-gray-400'}`}>
                  <CardContent className="pt-5 pb-4">
                    <p className="text-xs font-semibold uppercase tracking-wider">
                      {liquidazioneIva.saldo_iva > 0 ? 'IVA da Versare' : liquidazioneIva.saldo_iva < 0 ? 'Credito IVA' : 'Saldo Neutro'}
                    </p>
                    <p className={`text-2xl font-bold tabular-nums mt-1 ${liquidazioneIva.saldo_iva > 0 ? 'text-red-600' : liquidazioneIva.saldo_iva < 0 ? 'text-emerald-600' : 'text-gray-600'}`}>
                      {formatCurrency(Math.abs(liquidazioneIva.saldo_iva || 0))}
                    </p>
                    <p className="text-xs text-muted-foreground">Scadenza: {liquidazioneIva.scadenza_versamento}</p>
                  </CardContent>
                </Card>
              </div>
              <div className="grid gap-6 md:grid-cols-2">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <ArrowDownRight className="h-4 w-4 text-red-600" /> Fatture di Vendita (IVA a Debito)
                    </CardTitle>
                    <CardDescription>{liquidazioneIva.iva_debito?.count || 0} documenti</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {(liquidazioneIva.iva_debito?.movimenti || []).length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">{t("invoices.noSalesInvoices")}</p>
                    ) : (
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {(liquidazioneIva.iva_debito?.movimenti || []).map(m => (
                          <div key={m.id} className="p-3 bg-red-50 border border-red-100 rounded-lg">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-sm">{m.numero}</p>
                                <p className="text-xs text-muted-foreground">{m.fornitore_cliente}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-red-600">{formatCurrency(m.iva)}</p>
                                <p className="text-xs text-muted-foreground">Imp. {formatCurrency(m.imponibile)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <ArrowUpRight className="h-4 w-4 text-emerald-600" /> Fatture di Acquisto (IVA a Credito)
                    </CardTitle>
                    <CardDescription>{liquidazioneIva.iva_credito?.count || 0} documenti</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {(liquidazioneIva.iva_credito?.movimenti || []).length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">{t("invoices.noPurchaseInvoices")}</p>
                    ) : (
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {(liquidazioneIva.iva_credito?.movimenti || []).map(m => (
                          <div key={m.id} className="p-3 bg-emerald-50 border border-emerald-100 rounded-lg">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-sm">{m.numero}</p>
                                <p className="text-xs text-muted-foreground">{m.fornitore_cliente}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-emerald-600">{formatCurrency(m.iva)}</p>
                                <p className="text-xs text-muted-foreground">Imp. {formatCurrency(m.imponibile)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <Receipt className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>{t("scheduler.selectMonthAndCalculate")}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
        </div>

        {/* Planner side panel (inline, non-modal) */}
        {plannerOpen && showActionBar && (
          <div className="w-[360px] shrink-0" data-testid="planner-panel">
            <Card className="sticky top-4 border-indigo-200 shadow-md">
              <CardHeader className="pb-3 border-b border-indigo-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Calculator className="h-5 w-5 text-indigo-600" />
                    Planner Liquidita
                  </CardTitle>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPlannerOpen(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Clicca <Calculator className="h-3 w-3 inline text-indigo-600" /> sulle righe per aggiungere</p>
              </CardHeader>
              <CardContent className="pt-4 space-y-4 max-h-[calc(100vh-180px)] overflow-y-auto">
                {/* Saldi Banche */}
                <div className="space-y-2" data-testid="planner-banche">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <Landmark className="h-3.5 w-3.5" /> Saldi Banche
                  </Label>
                  {plannerLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Caricamento saldi...
                    </div>
                  ) : bancheData.length > 0 ? (
                    <div className="space-y-1.5">
                      {bancheData.map(b => (
                        <div key={b.id} className="flex items-center justify-between px-3 py-1.5 bg-slate-50 rounded-lg text-sm">
                          <span className="text-muted-foreground truncate mr-2 text-xs">{b.nome}</span>
                          <span className={`font-semibold tabular-nums text-xs ${b.saldo >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {formatCurrency(b.saldo)}
                          </span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between px-3 py-1.5 bg-indigo-50 rounded-lg border border-indigo-100">
                        <span className="text-xs font-semibold text-indigo-700">{t("treasury.totalBanks")}</span>
                        <span className="font-bold tabular-nums text-xs text-indigo-700">
                          {formatCurrency(bancheData.reduce((s, b) => s + (b.saldo || 0), 0))}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">Nessuna banca configurata</p>
                  )}

                  {/* Override manuale */}
                  <div className="flex items-center gap-2 pt-1">
                    <Label className="text-xs text-muted-foreground whitespace-nowrap">Saldo iniziale:</Label>
                    <Input
                      type="number"
                      step="0.01"
                      className="h-7 text-xs"
                      placeholder={formatCurrency(bancheData.reduce((s, b) => s + (b.saldo || 0), 0))}
                      value={saldoManuale !== null ? saldoManuale : ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSaldoManuale(v === '' ? null : parseFloat(v));
                      }}
                      data-testid="planner-saldo-manuale"
                    />
                    {saldoManuale !== null && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => setSaldoManuale(null)}>
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Saldo iniziale */}
                <Card className="bg-indigo-50 border-indigo-200" data-testid="planner-saldo-iniziale">
                  <CardContent className="py-2.5 flex items-center justify-between">
                    <span className="text-xs font-medium text-indigo-700">Saldo Iniziale</span>
                    <span className={`text-sm font-bold tabular-nums ${saldoIniziale >= 0 ? 'text-indigo-700' : 'text-red-600'}`}>
                      {formatCurrency(saldoIniziale)}
                    </span>
                  </CardContent>
                </Card>

                {/* Scadenze nel planner */}
                <div className="space-y-1" data-testid="planner-items">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Scadenze Pianificate ({plannerItems.length})
                    </Label>
                    {plannerItems.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-[10px] text-red-500 hover:text-red-700 hover:bg-red-50 px-2"
                        onClick={() => setPlannerItems([])}
                        data-testid="planner-clear-all"
                      >
                        <Trash2 className="h-3 w-3 mr-1" /> Svuota
                      </Button>
                    )}
                  </div>
                  {plannerRunning.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground">
                      <Calculator className="h-6 w-6 mx-auto mb-1 opacity-30" />
                      <p className="text-xs">Clicca l&apos;icona calcolatrice sulle righe</p>
                    </div>
                  ) : (
                    <div className="space-y-1 max-h-[40vh] overflow-y-auto pr-1">
                      {plannerRunning.map((item, idx) => {
                        const credito = isCredito(item);
                        const absImporto = Math.abs(item.importo || 0);
                        return (
                          <div
                            key={item._uid}
                            className="group flex items-start gap-1.5 px-2 py-1.5 rounded-lg border bg-white hover:bg-slate-50 transition-colors"
                            data-testid={`planner-item-${idx}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1">
                                {credito ? (
                                  <ArrowUpRight className="h-3 w-3 text-emerald-500 shrink-0" />
                                ) : (
                                  <ArrowDownRight className="h-3 w-3 text-red-500 shrink-0" />
                                )}
                                <span className="text-[11px] font-medium truncate">{item.descrizione}</span>
                              </div>
                              <div className="flex items-center gap-2 mt-0.5 pl-4">
                                <span className="text-[10px] text-muted-foreground">{formatDate(item.data_scadenza)}</span>
                                <span className={`text-[11px] font-semibold tabular-nums ${credito ? 'text-emerald-600' : 'text-red-600'}`}>
                                  {credito ? '+' : '-'}{formatCurrency(absImporto)}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col items-end shrink-0 gap-0.5">
                              <span className={`text-[11px] font-bold tabular-nums ${item._runningBalance >= 0 ? 'text-slate-700' : 'text-red-600'}`}>
                                {formatCurrency(item._runningBalance)}
                              </span>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-red-400 hover:text-red-600 hover:bg-red-50"
                                onClick={() => removePlannerItem(item._uid)}
                                title={t("common.delete")}
                                data-testid={`planner-remove-${idx}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Saldo finale */}
                <Card
                  className={saldoFinale >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}
                  data-testid="planner-saldo-finale"
                >
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Saldo Finale Previsto</p>
                        <p className={`text-xl font-bold tabular-nums mt-0.5 ${saldoFinale >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                          {formatCurrency(saldoFinale)}
                        </p>
                      </div>
                      {saldoFinale < 0 && <AlertTriangle className="h-6 w-6 text-red-400" />}
                      {saldoFinale >= 0 && <CheckCircle2 className="h-6 w-6 text-emerald-400" />}
                    </div>
                    {plannerItems.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-current/10 grid grid-cols-2 gap-2 text-[10px]">
                        <div>
                          <span className="text-muted-foreground">Uscite: </span>
                          <span className="font-semibold text-red-600">
                            {formatCurrency(plannerItems.filter(i => !isCredito(i)).reduce((s, i) => s + Math.abs(i.importo || 0), 0))}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Entrate: </span>
                          <span className="font-semibold text-emerald-600">
                            {formatCurrency(plannerItems.filter(i => isCredito(i)).reduce((s, i) => s + Math.abs(i.importo || 0), 0))}
                          </span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Clear planner */}
                {plannerItems.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-muted-foreground"
                    onClick={() => setPlannerItems([])}
                    data-testid="planner-clear-btn"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-2" />
                    Svuota Planner
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Approval Dialog */}
      <Dialog open={approvalDialog} onOpenChange={setApprovalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("treasury.approvePayment")}</DialogTitle>
            <DialogDescription>
              Conferma l&apos;approvazione di {selectedIds.size} scadenze per il pagamento.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="rounded-lg bg-muted p-4">
              <div className="text-sm text-muted-foreground mb-2">Totale selezionato:</div>
              <div className="text-2xl font-bold">
                {formatCurrency(currentViewItems.filter(s => selectedIds.has(s._uid)).reduce((sum, s) => sum + Math.abs(s.importo), 0))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleApprovePayment} disabled={approving}>
              {approving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Approva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={addDialog} onOpenChange={(open) => { setAddDialog(open); if (!open) { setEditingItem(null); resetForm(); }}}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Modifica Scadenza' : 'Nuova Scadenza Programmata'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Descrizione *</Label>
                <Input value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} data-testid="form-descrizione" />
              </div>
              <div>
                <Label>{t('common.amount')} *</Label>
                <Input type="number" step="0.01" value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })} data-testid="form-importo" />
              </div>
              <div>
                <Label>Data Scadenza *</Label>
                <DateInput value={form.data_scadenza} onChange={(e) => setForm({ ...form, data_scadenza: e.target.value })} />
              </div>
              <div>
                <Label>{t("common.type")}</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prestito">{t('common.loan')}</SelectItem>
                    <SelectItem value="finanziamento">{t('common.financing')}</SelectItem>
                    <SelectItem value="affitto">{t('common.rent')}</SelectItem>
                    <SelectItem value="assicurazione">{t('common.insurance')}</SelectItem>
                    <SelectItem value="bollo">Bollo</SelectItem>
                    <SelectItem value="altro">{t('common.other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('common.flow')}</Label>
                <Select value={form.tipo_flusso} onValueChange={(v) => setForm({ ...form, tipo_flusso: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="uscita">{t('dialogs.expense')}</SelectItem>
                    <SelectItem value="entrata">{t('dialogs.income')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>{t('common.beneficiary')}</Label>
                <Input value={form.beneficiario} onChange={(e) => setForm({ ...form, beneficiario: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>{t("common.notes")}</Label>
                <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSaveScheduled} data-testid="form-save-btn">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              {editingItem ? 'Aggiorna' : 'Crea'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
